import React from 'react'
import { DateTime } from 'luxon'
import { useTimezone } from '../../contexts/timezone'
import {
    Box,
    Typography,
    Modal,
    Paper,
    Slide,
    IconButton,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Chip,
    makeStyles,
    useTheme,
    Avatar,
    Grid,
    Card,
    CardContent,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import {
    AttachMoney,
    Person,
    LocationOn,
    Schedule,
    Receipt,
    Payment,
    Add,
} from '@material-ui/icons'
import {
    TenantBill,
    BillStatusLabels,
    PaymentStatusLabels,
    PaymentMethodLabels,
    PaymentMethod,
} from '../../models/Rent/types'
import { useRent } from '../../hooks/useRent'
import { toast } from 'react-toastify'

interface TenantBillDetailModalProps {
    open: boolean
    onClose: () => void
    bill: TenantBill | null
    onBillUpdate?: (updatedBill: TenantBill) => void
}

const useStyles = makeStyles((theme) => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(2),
    },
    paper: {
        width: '95%',
        maxWidth: 1200,
        maxHeight: '95vh',
        display: 'flex',
        flexDirection: 'column',
        outline: 'none',
        borderRadius: theme.spacing(2),
        boxShadow: theme.shadows[24],
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(2),
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: theme.palette.primary.contrastText,
        borderRadius: `${theme.spacing(2)}px ${theme.spacing(2)}px 0 0`,
    },
    headerTitle: {
        display: 'flex',
        alignItems: 'center',
    },
    headerIcon: {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        padding: theme.spacing(1),
        borderRadius: '50%',
    },
    content: {
        flex: 1,
        overflow: 'auto',
        padding: theme.spacing(3),
        backgroundColor: theme.palette.background.default,
    },
    section: {
        marginBottom: theme.spacing(4),
    },
    sectionTitle: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(3),
        paddingBottom: theme.spacing(1.5),
        borderBottom: `2px solid ${theme.palette.divider}`,
    },
    sectionIcon: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        padding: theme.spacing(0.5),
        borderRadius: '50%',
    },
    infoGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        marginBottom: theme.spacing(2),
    },
    infoCard: {
        height: '100%',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.shadows[8],
        },
    },
    infoItem: {
        display: 'flex',
        flexDirection: 'column',
    },
    infoLabel: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: theme.spacing(0.5),
    },
    infoValue: {
        fontWeight: 600,
        fontSize: '1.1rem',
    },
    amountValue: {
        fontWeight: 700,
        fontSize: '1.2rem',
        color: theme.palette.primary.main,
    },
    statusChip: {
        marginTop: theme.spacing(0.5),
        fontWeight: 600,
    },
    table: {
        minWidth: 650,
        '& .MuiTableCell-head': {
            backgroundColor: theme.palette.grey[50],
            fontWeight: 600,
            fontSize: '0.875rem',
        },
        '& .MuiTableRow-root:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    amountCell: {
        fontWeight: 600,
        textAlign: 'right',
        color: theme.palette.primary.main,
    },
    dateCell: {
        whiteSpace: 'nowrap',
        fontWeight: 500,
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(6),
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.grey[50],
        borderRadius: theme.spacing(1),
        border: `2px dashed ${theme.palette.divider}`,
    },
    emptyStateIcon: {
        fontSize: 48,
        marginBottom: theme.spacing(2),
        color: theme.palette.grey[400],
    },
    summaryCard: {
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
        color: theme.palette.primary.contrastText,
        marginBottom: theme.spacing(3),
    },
    summaryCardContent: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(2, 3),
    },
    summaryText: {
        fontWeight: 600,
        fontSize: '1.1rem',
    },
    summaryAmount: {
        fontWeight: 700,
        fontSize: '1.5rem',
    },
    divider: {
        margin: theme.spacing(3, 0),
        height: 2,
        background: `linear-gradient(90deg, transparent 0%, ${theme.palette.primary.main} 50%, transparent 100%)`,
    },
    voidedRow: {
        textDecoration: 'line-through',
        opacity: 0.6,
        backgroundColor: theme.palette.grey[100],
    },
    voidedChip: {
        backgroundColor: theme.palette.grey[500],
        color: theme.palette.common.white,
        fontWeight: 600,
    },
}))

export const TenantBillDetailModal: React.FC<TenantBillDetailModalProps> = ({
    open,
    onClose,
    bill,
    onBillUpdate,
}) => {
    const classes = useStyles()
    const theme = useTheme()
    const {
        createPayment,
        voidBill,
        voidBillItem,
        voidPayment,
        createBillItem,
        createStripeLoginLink,
    } = useRent()

    // Bill item creation state
    const [billItemDialogOpen, setBillItemDialogOpen] = React.useState(false)
    const [billItemForm, setBillItemForm] = React.useState({
        description: '',
        amount: '',
    })
    const [isCreatingBillItem, setIsCreatingBillItem] = React.useState(false)

    // Payment creation state
    const [paymentDialogOpen, setPaymentDialogOpen] = React.useState(false)
    const [paymentForm, setPaymentForm] = React.useState({
        amount: '',
        payment_method: PaymentMethod.CASH,
        notes: '',
    })
    const [isSubmitting, setIsSubmitting] = React.useState(false)

    // Void state
    const [voidDialogOpen, setVoidDialogOpen] = React.useState(false)
    const [voidType, setVoidType] = React.useState<
        'bill' | 'billItem' | 'payment' | null
    >(null)
    const [voidTarget, setVoidTarget] = React.useState<any>(null)
    const [voidReason, setVoidReason] = React.useState('')
    const [isVoiding, setIsVoiding] = React.useState(false)

    if (!bill) {
        return null
    }

    const handlePaymentFormChange = (
        field: string,
        value: string | PaymentMethod,
    ) => {
        setPaymentForm((prev) => ({
            ...prev,
            [field]: value,
        }))
    }

    const handleCreatePayment = async () => {
        if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
            return
        }

        setIsSubmitting(true)
        try {
            const updatedBill = await createPayment({
                bill_id: bill.id,
                amount: parseFloat(paymentForm.amount),
                payment_method: paymentForm.payment_method,
                notes: paymentForm.notes,
            })

            // Reset form and close dialog
            setPaymentForm({
                amount: '',
                payment_method: PaymentMethod.CASH,
                notes: '',
            })
            setPaymentDialogOpen(false)

            // Show success toast
            toast.success('Payment created successfully!')

            // Trigger bill update callback with the updated bill from API
            if (onBillUpdate && updatedBill) {
                onBillUpdate(updatedBill)
            }
        } catch (error: any) {
            console.error('Error creating payment:', error)
            // Show error toast
            const errorMessage =
                error.response?.data?.message ||
                'Failed to create payment. Please try again.'
            toast.error(errorMessage)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleOpenPaymentDialog = () => {
        setPaymentForm({
            amount: bill.balance_due.toString(),
            payment_method: PaymentMethod.CASH,
            notes: '',
        })
        setPaymentDialogOpen(true)
    }

    const handleClosePaymentDialog = () => {
        setPaymentDialogOpen(false)
        setPaymentForm({
            amount: '',
            payment_method: PaymentMethod.CASH,
            notes: '',
        })
    }

    const handleBillItemFormChange = (field: string, value: string) => {
        setBillItemForm((prev) => ({
            ...prev,
            [field]: value,
        }))
    }

    const handleOpenBillItemDialog = () => {
        setBillItemForm({
            description: '',
            amount: '',
        })
        setBillItemDialogOpen(true)
    }

    const handleCloseBillItemDialog = () => {
        setBillItemDialogOpen(false)
        setBillItemForm({
            description: '',
            amount: '',
        })
    }

    const handleCreateBillItem = async () => {
        if (
            !billItemForm.description ||
            !billItemForm.amount ||
            parseFloat(billItemForm.amount) <= 0
        ) {
            return
        }

        setIsCreatingBillItem(true)
        try {
            const updatedBill = await createBillItem({
                bill_id: bill.id,
                description: billItemForm.description,
                amount: parseFloat(billItemForm.amount),
            })

            // Reset form and close dialog
            setBillItemForm({
                description: '',
                amount: '',
            })
            setBillItemDialogOpen(false)

            // Show success toast
            toast.success('Bill item created successfully!')

            // Trigger bill update callback with the updated bill from API
            if (onBillUpdate && updatedBill) {
                onBillUpdate(updatedBill)
            }
        } catch (error: any) {
            console.error('Error creating bill item:', error)
            const errorMessage =
                error.response?.data?.message ||
                'Failed to create bill item. Please try again.'
            toast.error(errorMessage)
        } finally {
            setIsCreatingBillItem(false)
        }
    }

    const handleOpenVoidDialog = (
        type: 'bill' | 'billItem' | 'payment',
        target: any,
    ) => {
        setVoidType(type)
        setVoidTarget(target)
        setVoidReason('')
        setVoidDialogOpen(true)
    }

    const handleCloseVoidDialog = () => {
        setVoidDialogOpen(false)
        setVoidType(null)
        setVoidTarget(null)
        setVoidReason('')
    }

    const handleVoid = async () => {
        if (!voidType || !voidTarget) return

        setIsVoiding(true)
        try {
            let updatedBill: TenantBill

            switch (voidType) {
                case 'bill':
                    updatedBill = await voidBill({
                        bill_id: bill.id,
                        void_reason: voidReason || 'Voided by apartment',
                    })
                    break
                case 'billItem':
                    updatedBill = await voidBillItem({
                        bill_id: bill.id,
                        bill_item_id: voidTarget.id,
                        void_reason: voidReason || 'Item voided by apartment',
                    })
                    break
                case 'payment':
                    updatedBill = await voidPayment({
                        bill_id: bill.id,
                        payment_id: voidTarget.payment_id,
                        void_reason:
                            voidReason || 'Payment voided by apartment',
                    })
                    break
                default:
                    throw new Error('Invalid void type')
            }

            // Show success toast
            const voidTypeLabel =
                voidType === 'bill'
                    ? 'Bill'
                    : voidType === 'billItem'
                    ? 'Bill Item'
                    : 'Payment'
            toast.success(`${voidTypeLabel} voided successfully!`)

            // Trigger bill update callback
            if (onBillUpdate && updatedBill) {
                onBillUpdate(updatedBill)
            }

            // Close dialog
            handleCloseVoidDialog()
        } catch (error: any) {
            console.error('Error voiding:', error)
            const errorMessage =
                error.response?.data?.message ||
                'Failed to void. Please try again.'
            toast.error(errorMessage)
        } finally {
            setIsVoiding(false)
        }
    }

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount)
    }

    const { currentTimezone } = useTimezone()

    const formatDate = (
        dateString: string | null,
        isTimestamp: boolean = false,
    ) => {
        if (!dateString) return 'N/A'

        if (isTimestamp) {
            // For timestamps like created_at and billing_month
            return DateTime.fromISO(dateString)
                .setZone(currentTimezone)
                .toLocaleString({
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                })
        }

        try {
            // For plain dates like due_date
            const [year, month, day] = dateString.split('-').map(Number)
            if (!year || !month || !day) return 'N/A'

            const date = new Date(year, month - 1, day)
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            })
        } catch (error) {
            console.error('Error formatting date:', error)
            return 'N/A'
        }
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'paid':
                return 'primary'
            case 'overdue':
                return 'secondary'
            case 'sent':
                return 'secondary'
            case 'draft':
                return 'default'
            case 'voided':
                return 'default'
            case 'partial':
                return 'primary'
            default:
                return 'default'
        }
    }

    const getStatusBackgroundColor = (status: string) => {
        switch (status) {
            case 'paid':
                return theme.palette.success.main
            case 'overdue':
                return theme.palette.error.main
            case 'sent':
                return theme.palette.info.main
            case 'draft':
                return theme.palette.grey[500]
            case 'voided':
                return theme.palette.grey[400]
            case 'partial':
                return theme.palette.warning.main
            default:
                return theme.palette.grey[500]
        }
    }

    return (
        <Modal open={open} onClose={onClose} className={classes.modal}>
            <React.Fragment>
                <Slide direction="up" in={open}>
                    <Paper className={classes.paper}>
                        {/* Header */}
                        <Box className={classes.header}>
                            <Box className={classes.headerTitle}>
                                <Box
                                    className={classes.headerIcon}
                                    style={{ marginRight: theme.spacing(2) }}
                                >
                                    <AttachMoney />
                                </Box>
                                <Typography
                                    variant="h6"
                                    style={{ fontWeight: 600 }}
                                >
                                    Bill Details
                                </Typography>
                            </Box>
                            <Box
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: theme.spacing(1),
                                }}
                            >
                                {!bill.is_voided && (
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={() =>
                                            handleOpenVoidDialog('bill', bill)
                                        }
                                        style={{
                                            color:
                                                theme.palette.primary
                                                    .contrastText,
                                            borderColor:
                                                theme.palette.primary
                                                    .contrastText,
                                        }}
                                    >
                                        Void Bill
                                    </Button>
                                )}
                                <IconButton
                                    onClick={onClose}
                                    style={{
                                        color:
                                            theme.palette.primary.contrastText,
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        {/* Content */}
                        <Box className={classes.content}>
                            {/* Summary Card */}
                            <Card className={classes.summaryCard}>
                                <CardContent
                                    className={classes.summaryCardContent}
                                >
                                    <Box>
                                        <Typography
                                            className={classes.summaryText}
                                        >
                                            Balance Due
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            style={{ opacity: 0.9 }}
                                        >
                                            Due: {formatDate(bill.due_date)}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        className={classes.summaryAmount}
                                    >
                                        {formatCurrency(bill.balance_due)}
                                    </Typography>
                                </CardContent>
                            </Card>

                            {/* Tenant Info & Location Section */}
                            <Box className={classes.section}>
                                <Box className={classes.sectionTitle}>
                                    <Box
                                        className={classes.sectionIcon}
                                        style={{
                                            marginRight: theme.spacing(1.5),
                                        }}
                                    >
                                        <Person fontSize="small" />
                                    </Box>
                                    <Typography
                                        variant="h6"
                                        style={{ fontWeight: 600 }}
                                    >
                                        Tenant & Location Information
                                    </Typography>
                                </Box>

                                <Grid
                                    container
                                    spacing={3}
                                    style={{ marginBottom: theme.spacing(2) }}
                                >
                                    <Grid item xs={12} md={6} lg={4}>
                                        <Card className={classes.infoCard}>
                                            <CardContent>
                                                <Box
                                                    className={classes.infoItem}
                                                >
                                                    <Typography
                                                        className={
                                                            classes.infoLabel
                                                        }
                                                    >
                                                        Tenant
                                                    </Typography>
                                                    <Typography
                                                        className={
                                                            classes.infoValue
                                                        }
                                                    >
                                                        {bill.tenant.name}
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} md={6} lg={4}>
                                        <Card className={classes.infoCard}>
                                            <CardContent>
                                                <Box
                                                    className={classes.infoItem}
                                                >
                                                    <Typography
                                                        className={
                                                            classes.infoLabel
                                                        }
                                                    >
                                                        Unit
                                                    </Typography>
                                                    <Typography
                                                        className={
                                                            classes.infoValue
                                                        }
                                                    >
                                                        {
                                                            bill.lease.area.unit
                                                                .name
                                                        }
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} md={6} lg={4}>
                                        <Card className={classes.infoCard}>
                                            <CardContent>
                                                <Box
                                                    className={classes.infoItem}
                                                >
                                                    <Typography
                                                        className={
                                                            classes.infoLabel
                                                        }
                                                    >
                                                        Area
                                                    </Typography>
                                                    <Typography
                                                        className={
                                                            classes.infoValue
                                                        }
                                                    >
                                                        {
                                                            bill.lease.area
                                                                .area_config
                                                                .name
                                                        }{' '}
                                                        -{' '}
                                                        {
                                                            bill.lease.area
                                                                .area_label
                                                        }
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} md={6} lg={4}>
                                        <Card className={classes.infoCard}>
                                            <CardContent>
                                                <Box
                                                    className={classes.infoItem}
                                                >
                                                    <Typography
                                                        className={
                                                            classes.infoLabel
                                                        }
                                                    >
                                                        Sent Date
                                                    </Typography>
                                                    <Typography
                                                        className={
                                                            classes.infoValue
                                                        }
                                                    >
                                                        {formatDate(
                                                            bill.sent_at,
                                                            true,
                                                        )}
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} md={6} lg={4}>
                                        <Card className={classes.infoCard}>
                                            <CardContent>
                                                <Box
                                                    className={classes.infoItem}
                                                >
                                                    <Typography
                                                        className={
                                                            classes.infoLabel
                                                        }
                                                    >
                                                        Due Date
                                                    </Typography>
                                                    <Typography
                                                        className={
                                                            classes.infoValue
                                                        }
                                                    >
                                                        {formatDate(
                                                            bill.due_date,
                                                        )}
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} md={6} lg={4}>
                                        <Card className={classes.infoCard}>
                                            <CardContent>
                                                <Box
                                                    className={classes.infoItem}
                                                >
                                                    <Typography
                                                        className={
                                                            classes.infoLabel
                                                        }
                                                    >
                                                        Created Date
                                                    </Typography>
                                                    <Typography
                                                        className={
                                                            classes.infoValue
                                                        }
                                                    >
                                                        {formatDate(
                                                            bill.created_date,
                                                            true,
                                                        )}
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} md={6} lg={4}>
                                        <Card className={classes.infoCard}>
                                            <CardContent>
                                                <Box
                                                    className={classes.infoItem}
                                                >
                                                    <Typography
                                                        className={
                                                            classes.infoLabel
                                                        }
                                                    >
                                                        Status
                                                    </Typography>
                                                    <Chip
                                                        label={
                                                            BillStatusLabels[
                                                                bill.status
                                                            ]
                                                        }
                                                        style={{
                                                            backgroundColor: getStatusBackgroundColor(
                                                                bill.status,
                                                            ),
                                                            color:
                                                                theme.palette
                                                                    .common
                                                                    .white,
                                                            fontWeight: 600,
                                                        }}
                                                        size="small"
                                                        className={
                                                            classes.statusChip
                                                        }
                                                    />
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} md={6} lg={4}>
                                        <Card className={classes.infoCard}>
                                            <CardContent>
                                                <Box
                                                    className={classes.infoItem}
                                                >
                                                    <Typography
                                                        className={
                                                            classes.infoLabel
                                                        }
                                                    >
                                                        Total Amount
                                                    </Typography>
                                                    <Typography
                                                        className={
                                                            classes.amountValue
                                                        }
                                                    >
                                                        {formatCurrency(
                                                            bill.total_amount,
                                                        )}
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} md={6} lg={4}>
                                        <Card className={classes.infoCard}>
                                            <CardContent>
                                                <Box
                                                    className={classes.infoItem}
                                                >
                                                    <Typography
                                                        className={
                                                            classes.infoLabel
                                                        }
                                                    >
                                                        Amount Paid
                                                    </Typography>
                                                    <Typography
                                                        className={
                                                            classes.amountValue
                                                        }
                                                    >
                                                        {formatCurrency(
                                                            bill.amount_paid,
                                                        )}
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Box>

                            <Divider className={classes.divider} />

                            {/* Billing Items Section */}
                            <Box className={classes.section}>
                                <Box className={classes.sectionTitle}>
                                    <Box
                                        className={classes.sectionIcon}
                                        style={{
                                            marginRight: theme.spacing(1.5),
                                        }}
                                    >
                                        <Receipt fontSize="small" />
                                    </Box>
                                    <Typography
                                        variant="h6"
                                        style={{ fontWeight: 600 }}
                                    >
                                        Billing Items
                                    </Typography>
                                    <Box style={{ marginLeft: 'auto' }}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<Add />}
                                            onClick={handleOpenBillItemDialog}
                                            disabled={bill.is_voided}
                                        >
                                            Add Bill Item
                                        </Button>
                                    </Box>
                                </Box>

                                {bill.bill_items.length > 0 ? (
                                    <TableContainer
                                        component={Paper}
                                        elevation={2}
                                    >
                                        <Table className={classes.table}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>
                                                        Description
                                                    </TableCell>
                                                    <TableCell>
                                                        Frequency
                                                    </TableCell>
                                                    <TableCell>
                                                        Period
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        Amount
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        Actions
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {bill.bill_items.map((item) => (
                                                    <TableRow
                                                        key={item.id}
                                                        className={
                                                            item.is_voided
                                                                ? classes.voidedRow
                                                                : ''
                                                        }
                                                    >
                                                        <TableCell>
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                            >
                                                                {
                                                                    item.description
                                                                }
                                                                {item.is_voided && (
                                                                    <Chip
                                                                        label="VOIDED"
                                                                        size="small"
                                                                        className={
                                                                            classes.voidedChip
                                                                        }
                                                                        style={{
                                                                            marginLeft: theme.spacing(
                                                                                1,
                                                                            ),
                                                                        }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            {item.frequency}
                                                        </TableCell>
                                                        <TableCell
                                                            className={
                                                                classes.dateCell
                                                            }
                                                        >
                                                            {formatDate(
                                                                item.period_start,
                                                            )}{' '}
                                                            -{' '}
                                                            {formatDate(
                                                                item.period_end,
                                                            )}
                                                        </TableCell>
                                                        <TableCell
                                                            className={
                                                                classes.amountCell
                                                            }
                                                        >
                                                            {formatCurrency(
                                                                item.amount,
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {!bill.is_voided &&
                                                                !item.is_voided && (
                                                                    <Button
                                                                        variant="outlined"
                                                                        color="secondary"
                                                                        size="small"
                                                                        onClick={() =>
                                                                            handleOpenVoidDialog(
                                                                                'billItem',
                                                                                item,
                                                                            )
                                                                        }
                                                                    >
                                                                        Void
                                                                    </Button>
                                                                )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <Box className={classes.emptyState}>
                                        <Receipt
                                            className={classes.emptyStateIcon}
                                        />
                                        <Typography
                                            variant="h6"
                                            style={{
                                                marginBottom: theme.spacing(1),
                                            }}
                                        >
                                            No Billing Items
                                        </Typography>
                                        <Typography variant="body2">
                                            No billing items have been added to
                                            this bill yet.
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            <Divider className={classes.divider} />

                            {/* Payments Section */}
                            <Box className={classes.section}>
                                <Box className={classes.sectionTitle}>
                                    <Box
                                        className={classes.sectionIcon}
                                        style={{
                                            marginRight: theme.spacing(1.5),
                                        }}
                                    >
                                        <Payment fontSize="small" />
                                    </Box>
                                    <Typography
                                        variant="h6"
                                        style={{ fontWeight: 600 }}
                                    >
                                        Payments
                                    </Typography>
                                    <Box style={{ marginLeft: 'auto' }}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<Add />}
                                            onClick={handleOpenPaymentDialog}
                                            disabled={bill.balance_due <= 0}
                                        >
                                            Create Payment
                                        </Button>
                                    </Box>
                                </Box>

                                {bill.payments.length > 0 ? (
                                    <TableContainer
                                        component={Paper}
                                        elevation={2}
                                    >
                                        <Table className={classes.table}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>
                                                        Payment Date
                                                    </TableCell>
                                                    <TableCell>
                                                        Method
                                                    </TableCell>
                                                    <TableCell>
                                                        Status
                                                    </TableCell>
                                                    <TableCell>Notes</TableCell>
                                                    <TableCell align="right">
                                                        Amount
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        Actions
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {bill.payments.map(
                                                    (payment) => (
                                                        <TableRow
                                                            key={payment.id}
                                                            className={
                                                                payment.is_voided
                                                                    ? classes.voidedRow
                                                                    : ''
                                                            }
                                                        >
                                                            <TableCell
                                                                className={
                                                                    classes.dateCell
                                                                }
                                                            >
                                                                {formatDate(
                                                                    payment.payment_date,
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                {
                                                                    PaymentMethodLabels[
                                                                        payment
                                                                            .payment_method
                                                                    ]
                                                                }
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box
                                                                    display="flex"
                                                                    alignItems="center"
                                                                >
                                                                    <Chip
                                                                        label={
                                                                            PaymentStatusLabels[
                                                                                payment
                                                                                    .status
                                                                            ]
                                                                        }
                                                                        color={
                                                                            payment.status ===
                                                                            'succeeded'
                                                                                ? 'primary'
                                                                                : 'default'
                                                                        }
                                                                        size="small"
                                                                        style={{
                                                                            fontWeight: 600,
                                                                        }}
                                                                    />
                                                                    {payment.is_voided && (
                                                                        <Chip
                                                                            label="VOIDED"
                                                                            size="small"
                                                                            className={
                                                                                classes.voidedChip
                                                                            }
                                                                            style={{
                                                                                marginLeft: theme.spacing(
                                                                                    1,
                                                                                ),
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>
                                                                {payment.notes ||
                                                                    '-'}
                                                            </TableCell>
                                                            <TableCell
                                                                className={
                                                                    classes.amountCell
                                                                }
                                                            >
                                                                {formatCurrency(
                                                                    payment.amount,
                                                                )}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {!bill.is_voided &&
                                                                    payment.status ===
                                                                        'succeeded' &&
                                                                    !payment.is_voided && (
                                                                        <Button
                                                                            variant="outlined"
                                                                            color="secondary"
                                                                            size="small"
                                                                            onClick={() =>
                                                                                handleOpenVoidDialog(
                                                                                    'payment',
                                                                                    payment,
                                                                                )
                                                                            }
                                                                        >
                                                                            Void
                                                                        </Button>
                                                                    )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ),
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <Box className={classes.emptyState}>
                                        <Payment
                                            className={classes.emptyStateIcon}
                                        />
                                        <Typography
                                            variant="h6"
                                            style={{
                                                marginBottom: theme.spacing(1),
                                            }}
                                        >
                                            No Payments
                                        </Typography>
                                        <Typography variant="body2">
                                            No payments have been made for this
                                            bill yet.
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Paper>
                </Slide>

                {/* Payment Creation Dialog */}
                <Dialog
                    open={paymentDialogOpen}
                    onClose={handleClosePaymentDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        <Box
                            display="flex"
                            alignItems="center"
                            style={{ marginRight: theme.spacing(1) }}
                        >
                            <Payment
                                color="primary"
                                style={{ marginRight: theme.spacing(1) }}
                            />
                            Create Payment
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Box
                            display="flex"
                            flexDirection="column"
                            style={{ paddingTop: theme.spacing(1) }}
                        >
                            <TextField
                                label="Amount"
                                type="number"
                                value={paymentForm.amount}
                                onChange={(e) =>
                                    handlePaymentFormChange(
                                        'amount',
                                        e.target.value,
                                    )
                                }
                                fullWidth
                                required
                                style={{ marginBottom: theme.spacing(3) }}
                                inputProps={{
                                    min: 0,
                                    step: 0.01,
                                    max: bill.balance_due,
                                }}
                                helperText={`Maximum amount: ${formatCurrency(
                                    bill.balance_due,
                                )}`}
                            />

                            <FormControl
                                fullWidth
                                required
                                style={{ marginBottom: theme.spacing(3) }}
                            >
                                <InputLabel>Payment Method</InputLabel>
                                <Select
                                    value={paymentForm.payment_method}
                                    onChange={(e) =>
                                        handlePaymentFormChange(
                                            'payment_method',
                                            e.target.value as PaymentMethod,
                                        )
                                    }
                                    label="Payment Method"
                                >
                                    {Object.entries(PaymentMethodLabels).map(
                                        ([value, label]) => (
                                            <MenuItem key={value} value={value}>
                                                {label}
                                            </MenuItem>
                                        ),
                                    )}
                                </Select>
                            </FormControl>

                            <TextField
                                label="Notes (Optional)"
                                value={paymentForm.notes}
                                onChange={(e) =>
                                    handlePaymentFormChange(
                                        'notes',
                                        e.target.value,
                                    )
                                }
                                fullWidth
                                multiline
                                rows={3}
                                placeholder="Add any notes about this payment..."
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={handleClosePaymentDialog}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreatePayment}
                            variant="contained"
                            color="primary"
                            disabled={
                                isSubmitting ||
                                !paymentForm.amount ||
                                parseFloat(paymentForm.amount) <= 0
                            }
                        >
                            {isSubmitting ? 'Creating...' : 'Create Payment'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Void Confirmation Dialog */}
                <Dialog
                    open={voidDialogOpen}
                    onClose={handleCloseVoidDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        <Box
                            display="flex"
                            alignItems="center"
                            style={{ marginRight: theme.spacing(1) }}
                        >
                            <Typography
                                color="error"
                                style={{ marginRight: theme.spacing(1) }}
                            >
                                ⚠️
                            </Typography>
                            Void{' '}
                            {voidType === 'bill'
                                ? 'Bill'
                                : voidType === 'billItem'
                                ? 'Bill Item'
                                : 'Payment'}
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Box
                            display="flex"
                            flexDirection="column"
                            style={{ paddingTop: theme.spacing(1) }}
                        >
                            <Typography
                                variant="body1"
                                style={{ marginBottom: theme.spacing(2) }}
                            >
                                Are you sure you want to void this{' '}
                                {voidType === 'bill'
                                    ? 'bill'
                                    : voidType === 'billItem'
                                    ? 'bill item'
                                    : 'payment'}
                                ? This action cannot be undone.
                            </Typography>

                            <TextField
                                label="Void Reason (Optional)"
                                value={voidReason}
                                onChange={(e) => setVoidReason(e.target.value)}
                                fullWidth
                                multiline
                                rows={3}
                                placeholder="Enter a reason for voiding..."
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={handleCloseVoidDialog}
                            disabled={isVoiding}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleVoid}
                            variant="contained"
                            color="secondary"
                            disabled={isVoiding}
                        >
                            {isVoiding ? 'Voiding...' : 'Void'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Bill Item Creation Dialog */}
                <Dialog
                    open={billItemDialogOpen}
                    onClose={handleCloseBillItemDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        <Box
                            display="flex"
                            alignItems="center"
                            style={{ marginRight: theme.spacing(1) }}
                        >
                            <Receipt
                                color="primary"
                                style={{ marginRight: theme.spacing(1) }}
                            />
                            Add Bill Item
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Box
                            display="flex"
                            flexDirection="column"
                            style={{ paddingTop: theme.spacing(1) }}
                        >
                            <TextField
                                label="Description"
                                value={billItemForm.description}
                                onChange={(e) =>
                                    handleBillItemFormChange(
                                        'description',
                                        e.target.value,
                                    )
                                }
                                fullWidth
                                required
                                style={{ marginBottom: theme.spacing(3) }}
                            />

                            <TextField
                                label="Amount"
                                type="number"
                                value={billItemForm.amount}
                                onChange={(e) =>
                                    handleBillItemFormChange(
                                        'amount',
                                        e.target.value,
                                    )
                                }
                                fullWidth
                                required
                                inputProps={{
                                    min: 0,
                                    step: 0.01,
                                }}
                                helperText="Enter the amount for this bill item"
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={handleCloseBillItemDialog}
                            disabled={isCreatingBillItem}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreateBillItem}
                            variant="contained"
                            color="primary"
                            disabled={
                                isCreatingBillItem ||
                                !billItemForm.description ||
                                !billItemForm.amount ||
                                parseFloat(billItemForm.amount) <= 0
                            }
                        >
                            {isCreatingBillItem
                                ? 'Creating...'
                                : 'Create Bill Item'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </React.Fragment>
        </Modal>
    )
}
